import { forwardRef, useCallback, useRef, useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Tooltip } from '../Tooltip';
import type { TabsProps, TabsVariant, TabsSize, Tab } from './Tabs.types';

/**
 * Get size-based styles
 */
const getSizeStyles = (size: TabsSize, theme: any) => {
  switch (size) {
    case 'sm':
      return {
        fontSize: theme.typography.fontSize.xs,
        padding: '6px 10px',
        gap: '4px',
        iconSize: '14px',
        height: '28px',
      };
    case 'lg':
      return {
        fontSize: theme.typography.fontSize.md,
        padding: '12px 20px',
        gap: '8px',
        iconSize: '20px',
        height: '44px',
      };
    case 'md':
    default:
      return {
        fontSize: theme.typography.fontSize.sm,
        padding: '8px 16px',
        gap: '6px',
        iconSize: '18px',
        height: '36px',
      };
  }
};

/**
 * Container for the tab list
 */
const TabsContainer = styled.div<{
  $fullWidth: boolean;
  $variant: TabsVariant;
  $scrollable: boolean;
  $size: TabsSize;
}>`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  position: relative;
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
  
  ${({ $scrollable }) =>
    $scrollable &&
    css`
      overflow-x: auto;
      overflow-y: hidden;
      scrollbar-width: thin;
      -webkit-overflow-scrolling: touch;
      
      &::-webkit-scrollbar {
        height: 4px;
      }
      
      &::-webkit-scrollbar-track {
        background: transparent;
      }
      
      &::-webkit-scrollbar-thumb {
        background-color: rgba(0, 0, 0, 0.2);
        border-radius: 2px;
      }
    `}
  
  ${({ $variant, $size, theme }) => {
    const sizeStyles = getSizeStyles($size, theme);
    
    switch ($variant) {
      case 'underline':
        return css`
          border-bottom: 1px solid ${theme.colors.border.medium};
        `;
      case 'pills':
      case 'primary':
        return css`
          background-color: rgba(0, 0, 0, 0.05);
          border-radius: ${parseInt(sizeStyles.height) / 2}px;
          padding: 0;
          gap: 0;
        `;
      default:
        return css`
          gap: 0;
        `;
    }
  }}
`;

/**
 * Slider indicator for pills/primary variant
 */
const TabSlider = styled.div<{
  $left: number;
  $width: number;
  $primary: boolean;
  $disabled: boolean;
  $isInitialRender: boolean;
  $height: number;
}>`
  position: absolute;
  top: 0;
  z-index: 1;
  height: ${({ $height }) => $height}px;
  width: ${({ $width }) => $width}px;
  left: ${({ $left }) => $left}px;
  border-radius: ${({ $height }) => $height / 2}px;
  border: 1px solid ${({ $disabled, theme }) =>
    $disabled ? theme.colors.grey[200] : theme.colors.highlight};
  background-color: ${({ $disabled, $primary, theme }) => {
    if ($disabled) return theme.colors.white;
    if ($primary) return theme.colors.highlight;
    return theme.colors.white;
  }};
  transition: ${({ $isInitialRender }) =>
    $isInitialRender ? 'none' : 'width 0.3s ease, left 0.3s ease'};
  will-change: transform, width, left;
  pointer-events: none;
`;

/**
 * Individual tab button
 */
const TabButton = styled.button<{
  $isActive: boolean;
  $variant: TabsVariant;
  $size: TabsSize;
  $fullWidth: boolean;
  $iconOnly: boolean;
}>`
  position: relative;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
  background: transparent;
  cursor: pointer;
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  transition: color 0.2s ease;
  white-space: nowrap;
  flex: ${({ $fullWidth }) => ($fullWidth ? '1 1 0' : '0 0 auto')};
  min-width: ${({ $fullWidth }) => ($fullWidth ? '0' : 'auto')};
  
  /* Size styles */
  ${({ $size, $iconOnly, theme }) => {
    const sizeStyles = getSizeStyles($size, theme);
    return css`
      font-size: ${sizeStyles.fontSize};
      padding: ${$iconOnly ? `0 12px` : sizeStyles.padding};
      gap: ${sizeStyles.gap};
      height: ${sizeStyles.height};
    `;
  }}
  
  /* Variant styles */
  ${({ $variant, $isActive, theme }) => {
    switch ($variant) {
      case 'pills':
        return css`
          border-radius: 18px;
          color: ${$isActive ? theme.colors.grey[600] : theme.colors.grey[600]};
          
          &:hover:not(:disabled) {
            color: ${theme.colors.grey[800]};
          }
        `;
      case 'primary':
        return css`
          border-radius: 18px;
          color: ${$isActive ? theme.colors.white : theme.colors.grey[600]};
          
          &:hover:not(:disabled) {
            color: ${$isActive ? theme.colors.white : theme.colors.grey[800]};
          }
        `;
      case 'underline':
        return css`
          color: ${$isActive ? theme.colors.highlight : theme.colors.text.secondary};
          margin-bottom: -1px;
          
          &::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            height: 2px;
            background-color: ${$isActive ? theme.colors.highlight : 'transparent'};
            transition: background-color 0.2s;
          }
          
          &:hover:not(:disabled)::after {
            background-color: ${$isActive ? theme.colors.highlight : theme.colors.border.medium};
          }
          
          &:hover:not(:disabled) {
            color: ${$isActive ? theme.colors.highlight : theme.colors.text.primary};
          }
        `;
      default:
        return css`
          color: ${$isActive ? theme.colors.highlight : theme.colors.text.secondary};
          border-bottom: 2px solid ${$isActive ? theme.colors.highlight : 'transparent'};
          
          &:hover:not(:disabled) {
            color: ${$isActive ? theme.colors.highlight : theme.colors.text.primary};
            border-bottom-color: ${$isActive ? theme.colors.highlight : theme.colors.border.medium};
          }
        `;
    }
  }}
  
  /* Disabled state */
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
    color: ${({ theme }) => theme.colors.grey[400]};
  }
  
  /* Focus state - accessible */
  &:focus {
    outline: none;
  }
  
  &:focus-visible {
    border: 1px solid ${({ theme }) => theme.colors.grey[400]};
    ${({ $isActive }) =>
      $isActive &&
      css`
        outline: 2px solid rgba(0, 0, 0, 0.2);
      `}
  }
`;

/**
 * Icon wrapper for consistent sizing
 */
const IconWrapper = styled.span<{ $size: TabsSize }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  ${({ $size, theme }) => {
    const sizeStyles = getSizeStyles($size, theme);
    return css`
      width: ${sizeStyles.iconSize};
      height: ${sizeStyles.iconSize};
      
      svg {
        width: 100%;
        height: 100%;
      }
    `;
  }}
`;

/**
 * Badge for tab labels
 */
const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 6px;
  min-width: 18px;
  height: 18px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  border-radius: 9px;
  background-color: ${({ theme }) => theme.colors.highlight};
  color: ${({ theme }) => theme.colors.white};
  margin-left: 4px;
`;

/**
 * Tabs component - A navigation component for switching between views
 *
 * @example
 * ```tsx
 * const [activeTab, setActiveTab] = useState('tab1');
 * 
 * <Tabs
 *   tabs={[
 *     { id: 'tab1', label: 'Overview' },
 *     { id: 'tab2', label: 'Details', icon: <InfoIcon /> },
 *     { id: 'tab3', label: 'Settings', disabled: true },
 *     { id: 'tab4', label: 'Notifications', badgeText: '5' },
 *   ]}
 *   activeTab={activeTab}
 *   onTabChange={setActiveTab}
 *   variant="underline"
 *   size="md"
 * />
 * 
 * // Scrollable tabs for many items
 * <Tabs
 *   tabs={manyTabs}
 *   activeTab={activeTab}
 *   onTabChange={setActiveTab}
 *   scrollable
 * />
 * 
 * // Expanded tabs that fill width
 * <Tabs
 *   tabs={tabs}
 *   activeTab={activeTab}
 *   onTabChange={setActiveTab}
 *   expandTabs
 * />
 * 
 * // Primary (filled) variant
 * <Tabs
 *   tabs={tabs}
 *   activeTab={activeTab}
 *   onTabChange={setActiveTab}
 *   variant="primary"
 * />
 * ```
 */
export const Tabs = forwardRef<HTMLDivElement, TabsProps>(
  (
    {
      tabs,
      activeTab,
      onTabChange,
      variant = 'default',
      size = 'md',
      fullWidth = false,
      expandTabs = false,
      scrollable = false,
      className,
      'aria-label': ariaLabel = 'Tab navigation',
      'data-testid': testId,
    },
    ref
  ) => {
    const tabRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
    const containerRef = useRef<HTMLDivElement>(null);
    const [sliderStyle, setSliderStyle] = useState({ left: 0, width: 0 });
    const [isInitialRender, setIsInitialRender] = useState(true);
    
    // Use expandTabs or fullWidth (expandTabs takes precedence for ib-ui compatibility)
    const shouldExpand = expandTabs || fullWidth;
    
    // Is this a pill/primary variant that needs the slider?
    const useSlider = variant === 'pills' || variant === 'primary';
    
    // Update slider position when active tab changes
    useEffect(() => {
      if (!useSlider) return;
      
      const activeElement = tabRefs.current.get(activeTab);
      if (activeElement && containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const tabRect = activeElement.getBoundingClientRect();
        
        setSliderStyle({
          left: tabRect.left - containerRect.left,
          width: tabRect.width,
        });
      }
      
      // After first render, enable transitions
      if (isInitialRender) {
        requestAnimationFrame(() => {
          setIsInitialRender(false);
        });
      }
    }, [activeTab, useSlider, isInitialRender, tabs]);
    
    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLButtonElement>, currentIndex: number) => {
        const currentTab = tabs[currentIndex];
        if (!currentTab) return;
        
        const enabledTabs = tabs.filter(tab => !tab.disabled);
        const currentEnabledIndex = enabledTabs.findIndex(
          tab => tab.id === currentTab.id
        );
        
        let nextIndex: number | null = null;
        
        switch (event.key) {
          case 'ArrowLeft':
          case 'ArrowUp':
            event.preventDefault();
            nextIndex = currentEnabledIndex - 1;
            if (nextIndex < 0) nextIndex = enabledTabs.length - 1;
            break;
          case 'ArrowRight':
          case 'ArrowDown':
            event.preventDefault();
            nextIndex = currentEnabledIndex + 1;
            if (nextIndex >= enabledTabs.length) nextIndex = 0;
            break;
          case 'Home':
            event.preventDefault();
            nextIndex = 0;
            break;
          case 'End':
            event.preventDefault();
            nextIndex = enabledTabs.length - 1;
            break;
        }
        
        if (nextIndex !== null) {
          const nextTab = enabledTabs[nextIndex];
          if (nextTab) {
            const nextTabElement = tabRefs.current.get(nextTab.id);
            if (nextTabElement) {
              nextTabElement.focus();
              onTabChange(nextTab.id);
            }
          }
        }
      },
      [tabs, onTabChange]
    );
    
    const setTabRef = useCallback((id: string, element: HTMLButtonElement | null) => {
      if (element) {
        tabRefs.current.set(id, element);
      } else {
        tabRefs.current.delete(id);
      }
    }, []);
    
    const handleTabClick = useCallback(
      (tab: Tab, event: React.MouseEvent<HTMLButtonElement>) => {
        if (tab.disabled) return;
        tab.onClick?.(event);
        onTabChange(tab.id);
      },
      [onTabChange]
    );
    
    const sizeStyles = getSizeStyles(size, { typography: { fontSize: { xs: '12px', sm: '14px', md: '14.5px' } } });
    const activeTabData = tabs.find(t => t.id === activeTab);
    
    const renderTabButton = (tab: Tab, index: number) => {
      const isActive = tab.id === activeTab;
      const iconOnly = !!tab.icon && !tab.label;
      
      const button = (
        <TabButton
          key={tab.id}
          ref={(el) => setTabRef(tab.id, el)}
          role="tab"
          id={`tab-${tab.id}`}
          aria-selected={isActive}
          aria-controls={`tabpanel-${tab.id}`}
          tabIndex={isActive ? 0 : -1}
          disabled={tab.disabled}
          onClick={(e) => handleTabClick(tab, e)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          $isActive={isActive}
          $variant={variant}
          $size={size}
          $fullWidth={shouldExpand}
          $iconOnly={iconOnly}
          data-id={tab.dataId}
          data-testid={testId ? `${testId}-tab-${tab.id}` : undefined}
        >
          {tab.icon && (
            <IconWrapper $size={size} aria-hidden="true">
              {tab.icon}
            </IconWrapper>
          )}
          {tab.label}
          {tab.badgeText && <Badge>{tab.badgeText}</Badge>}
        </TabButton>
      );
      
      if (tab.tooltip) {
        return (
          <Tooltip key={tab.id} content={tab.tooltip}>
            {button}
          </Tooltip>
        );
      }
      
      return button;
    };
    
    return (
      <TabsContainer
        ref={(node) => {
          (containerRef as any).current = node;
          if (typeof ref === 'function') {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
        }}
        role="tablist"
        aria-label={ariaLabel}
        className={className}
        data-testid={testId}
        $fullWidth={shouldExpand}
        $variant={variant}
        $scrollable={scrollable}
        $size={size}
      >
        {useSlider && (
          <TabSlider
            $left={sliderStyle.left}
            $width={sliderStyle.width}
            $primary={variant === 'primary'}
            $disabled={!!activeTabData?.disabled}
            $isInitialRender={isInitialRender}
            $height={parseInt(sizeStyles.height)}
            aria-hidden="true"
          />
        )}
        {tabs.map((tab, index) => renderTabButton(tab, index))}
      </TabsContainer>
    );
  }
);

Tabs.displayName = 'Tabs';

export default Tabs;
